import { ReactNode, useRef, MouseEvent } from "react";
import { cn } from "@/lib/utils";

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  /** Max cursor pull in px. */
  strength?: number;
  asChild?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * Wrap any clickable element to give it a soft cursor-magnet effect.
 * Strength caps at 6px. Glow halo grows as cursor approaches.
 */
export function MagneticButton({
  children,
  className,
  strength = 6,
  onClick,
}: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    const tx = Math.max(-1, Math.min(1, dx)) * strength;
    const ty = Math.max(-1, Math.min(1, dy)) * strength;
    el.style.setProperty("--mag-x", `${tx}px`);
    el.style.setProperty("--mag-y", `${ty}px`);
    el.style.setProperty("--mag-glow", `${Math.min(1, Math.hypot(dx, dy))}`);
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--mag-x", `0px`);
    el.style.setProperty("--mag-y", `0px`);
    el.style.setProperty("--mag-glow", `0`);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      className={cn("magnetic-wrap inline-flex", className)}
      style={
        {
          transform: "translate3d(var(--mag-x,0), var(--mag-y,0), 0)",
          transition: "transform 220ms cubic-bezier(0.22,1,0.36,1)",
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
}
